import { cn } from '@/lib/cn';
import { Check, MoreHorizontal } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, type ButtonProps } from './Button';

/**
 * Menu „Wiecej” — akcje drugorzedne i nieodwracalne (usun, anuluj) zamiast
 * sciany kolorowych przyciskow (audyt LEAD-002, OF-008, PR-006).
 * Portal do <body> (nie ucina sie w kartach z overflow), pozycja przy
 * przycisku z korekta do krawedzi ekranu; ESC / klik poza / wybor zamyka.
 */
export interface ActionMenuItem {
  label: string;
  icon?: LucideIcon;
  onSelect: () => void;
  tone?: 'default' | 'danger';
  disabled?: boolean;
  hint?: string;
  /** Zaznaczona opcja (np. aktualna kategoria) — ptaszek po prawej. */
  checked?: boolean;
  /** Kolorowa kropka zamiast ikony (np. etykiety) — klasy tła, np. `bg-red-500`. */
  swatch?: string;
}

export function ActionMenu({
  items,
  label = 'Więcej akcji',
  trigger,
  triggerVariant = 'secondary',
  align = 'end',
  header,
  triggerClassName,
  triggerIcon = MoreHorizontal,
  triggerSize,
}: {
  items: ReadonlyArray<ActionMenuItem | 'separator'>;
  label?: string;
  /** Wlasny tekst przycisku; domyslnie sama ikona „…”. */
  trigger?: ReactNode;
  triggerVariant?: ButtonProps['variant'];
  align?: 'start' | 'end';
  /** Tresc nad pozycjami (np. imie i rola w menu profilu). */
  header?: ReactNode;
  triggerClassName?: string;
  /** Ikona przycisku bez tekstu (domyślnie „…”). */
  triggerIcon?: LucideIcon;
  /** Rozmiar przycisku — np. `icon-sm` dla małej kropki-wyzwalacza. */
  triggerSize?: ButtonProps['size'];
}) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const close = useCallback(() => setOpen(false), []);

  useLayoutEffect(() => {
    if (!open || !buttonRef.current || !menuRef.current) return;
    const b = buttonRef.current.getBoundingClientRect();
    const m = menuRef.current.getBoundingClientRect();
    const margin = 8;
    let left = align === 'end' ? b.right - m.width : b.left;
    left = Math.max(margin, Math.min(left, window.innerWidth - m.width - margin));
    let top = b.bottom + 6;
    if (top + m.height > window.innerHeight - margin) top = Math.max(margin, b.top - m.height - 6);
    setPos({ top, left });
  }, [open, align]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!menuRef.current?.contains(t) && !buttonRef.current?.contains(t)) close();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    window.addEventListener('resize', close);
    // Menu jest „fixed” — przy przewijaniu (okno albo <main> na desktopie) zamykamy,
    // zeby nie wisialo w miejscu oderwane od przycisku (Dawid 2026-09-30).
    const onScroll = (e: Event) => {
      if (!menuRef.current?.contains(e.target as Node)) close();
    };
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open, close]);

  return (
    <>
      <Button
        ref={buttonRef}
        variant={triggerVariant}
        size={triggerSize ?? (trigger ? 'md' : 'icon')}
        icon={trigger ? undefined : triggerIcon}
        className={triggerClassName}
        aria-haspopup="menu"
        aria-expanded={open}
        // Własny wyzwalacz bez tekstu (np. kropka etykiety, triggerSize) też potrzebuje nazwy.
        aria-label={!trigger || triggerSize ? label : undefined}
        title={!trigger || triggerSize ? label : undefined}
        onClick={() => {
          setPos(null);
          setOpen((o) => !o);
        }}
      >
        {trigger}
      </Button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999 }}
            className="fixed z-[60] min-w-[220px] max-w-[calc(100vw-16px)] animate-fade-in rounded-lg border border-border bg-surface p-1 shadow-overlay"
          >
            {header && <div className="border-b border-border px-3 pb-2.5 pt-2">{header}</div>}
            {items.map((item, i) =>
              item === 'separator' ? (
                // biome-ignore lint/suspicious/noArrayIndexKey: separatory nie maja id
                <div key={`sep-${i}`} className="my-1 h-px bg-border" role="separator" />
              ) : (
                <button
                  key={item.label}
                  type="button"
                  role="menuitem"
                  disabled={item.disabled}
                  onClick={() => {
                    close();
                    item.onSelect();
                  }}
                  className={cn(
                    'flex min-h-touch w-full items-center gap-3 rounded-md px-3 text-left text-sm transition-colors disabled:opacity-40 md:min-h-9',
                    item.tone === 'danger'
                      ? 'text-error hover:bg-error-subtle'
                      : 'text-fg hover:bg-surface-2',
                  )}
                >
                  {item.swatch && (
                    <span
                      className={cn('h-3 w-3 shrink-0 rounded-full', item.swatch)}
                      aria-hidden
                    />
                  )}
                  {item.icon && (
                    <item.icon
                      className={cn(
                        'h-4 w-4 shrink-0',
                        item.tone === 'danger' ? '' : 'text-fg-muted',
                      )}
                      strokeWidth={2}
                    />
                  )}
                  <span className="flex-1">{item.label}</span>
                  {item.hint && <span className="text-xs text-fg-subtle">{item.hint}</span>}
                  {item.checked && (
                    <Check className="h-4 w-4 shrink-0 text-accent" strokeWidth={2.5} />
                  )}
                </button>
              ),
            )}
          </div>,
          document.body,
        )}
    </>
  );
}
